import { __rest, __assign } from 'tslib';
import * as React from 'react';
import { UitkLayoutTags } from 'uitk-react-layout';
import { classNames } from 'uitk-react-utils';
import styles from './uitk-layout-grid.css';

// --
// Grid types
// --
var UitkLayoutGridColumnRows = [1, 2, 3, 4, 6, 8, 12];
var EgdsSizingLengths = [
    '0x',
    '0x__half',
    '0x__quarter',
    '1x',
    '1x__half',
    '2x',
    '3x',
    '3x__half',
    '4x',
    '4x__half',
    '5x',
    '6x',
    '7x',
    '8x',
    '9x',
    '10x',
    '11x',
    '12x',
    '13x',
    '14x',
    '15x',
    '16x',
    '17x',
    '18x',
    '24x',
    '30x',
    '37x',
    '38x',
    '40x',
    '48x',
    '70x',
    '72x',
    '76x',
    '88x',
    '96x',
    '104x',
    '112x',
    '120x',
    '180x',
    '312x',
    '500x'
];
var UitkLayoutGridAligns = ['start', 'end', 'center', 'stretch'];
var UitkLayoutGridPlaces = [
    'start',
    'end',
    'center',
    'stretch',
    'space-around',
    'space-between',
    'space-evenly'
];
var UitkLayoutGridAutoFlows = ['row', 'column', 'dense', 'row-dense', 'column-dense'];
var UitkLayoutGridBlockSizes = ['full_size'];
// --
// Grid item types
// --
var UitkLayoutGridSpans = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
var UitkLayoutGridItemPlaceSelfs = ['start', 'end', 'center', 'stretch'];

/**
 * Check string is a valid EgdsSizingLength value
 *
 * @param size {string} - size
 * @returns EgdsSizingLengths.includes(size as EgdsSizingLength)
 */
var IsEgdsSize = function (size) {
    return EgdsSizingLengths.includes(size);
};
/**
 * Check for deprecated "auto_fit" or "auto_fill" keys
 *
 * @param obj {UitkLayoutConditionalGridTrack} - object
 * @returns boolean
 */
var isAutoRepeatGrid = function (obj) {
    return Object.prototype.hasOwnProperty.call(obj, 'auto_fill') || Object.prototype.hasOwnProperty.call(obj, 'auto_fit');
};
/**
 * Outputs a css custom property for a valid EgdsSizingLength value
 *
 * @param value {EgdsSizingLength} - value
 * @returns `var(--uitk-layoutgrid-egds-size__${value})`
 */
var EgdsSizeCustomProp = function (value) { return "var(--uitk-layoutgrid-egds-size__" + value + ")"; };
/**
 * Outputs a css fit-content() function
 *
 * @param length {EgdsSizingLength} - length
 * @returns {`fit-content(${typeof fitContentValue})`} `fit-content(length)`
 */
var FitContent = function (length) {
    var fitContentValue = EgdsSizeCustomProp(length);
    var fitcontentValue = "fit-content(" + fitContentValue + ")";
    return fitcontentValue;
};
/**
 * Outputs a css minmax() function
 *
 * @param min {UitkLayoutGridLength} - min value
 * @param max {UitkLayoutGridLength} - max value
 * @returns {`minmax(${typeof minValue}, ${typeof maxValue})`} `minmax(min, max)`
 */
var MinMax = function (min, max) {
    var minValue = IsEgdsSize(min) ? EgdsSizeCustomProp(min) : min;
    var maxValue = IsEgdsSize(max) ? EgdsSizeCustomProp(max) : max;
    var minmaxValue = "minmax(" + minValue + ", " + maxValue + ")";
    return minmaxValue;
};
/**
 * Outputs a css repeat() function
 *
 * @param repeatType {number | 'auto-fit' | 'auto-fill'} - repeat type
 * @param track {UitkLayoutGridLength | ReturnType<typeof MinMax> | ReturnType<typeof FitContent>} - track to repeat
 * @returns {`repeat(${typeof repeatType}, ${typeof track})`} `repeat(repeatType, track)`
 */
var Repeat = function (repeatType, track) {
    var trackValue = IsEgdsSize(track) ? EgdsSizeCustomProp(track) : track;
    var repeatValue = "repeat(" + repeatType + ", " + trackValue + ")";
    return repeatValue;
};

var getClassNameList$1 = classNames.bind(styles);
var baseClass = 'uitk-layout-grid';
/**
 * Set a valid grid track value
 *
 * @param value {any}
 * @returns string
 */
var trackList = function (value) {
    var track = [];
    var isArray = Array.isArray(value);
    var isNumber = typeof value === 'number';
    if (isArray) {
        value.forEach(function (item) { return track.push(IsEgdsSize(item) ? EgdsSizeCustomProp(item) : item); });
    }
    else if (isNumber) {
        track.push("repeat(" + value + ", minmax(0, 1fr))"); // Include minmax() to ensure unbroken content doesn't force grid out
    }
    else {
        track.push(IsEgdsSize(value) ? EgdsSizeCustomProp(value) : value);
    }
    return track.join(' ');
};
/**
 * Generate a valid CSS custom prop / value object for grid areas
 *
 * @param areas {UitkLayoutGridArea | UitkLayoutConditionalGridArea}
 * @returns Object as React.CSSProperties
 */
var areasValue = function (areas) {
    var _a;
    var isObject = !Array.isArray(areas) && typeof areas === 'object';
    var obj = {};
    var areaRows = function (value) { return value.map(function (area) { return "\"" + area + "\""; }).join(' '); };
    if (isObject) {
        Object.entries(areas).map(function (_a) {
            var _b;
            var key = _a[0], val = _a[1];
            return Object.assign(obj, (_b = {}, _b["--uitk-layoutgrid-areas-" + key] = areaRows(val), _b));
        });
    }
    else {
        Object.assign(obj, (_a = {}, _a["--uitk-layoutgrid-areas"] = areaRows(areas), _a));
    }
    return obj;
};
/**
 * Generate a valid CSS custom prop / value object for grid gap
 *
 * @param value {UitkLayoutSpace | UitkLayoutGridSpaceInterface}
 * @returns Object as React.CSSProperties
 */
var spaceValue = function (value) {
    var _a, _b;
    var isString = typeof value === 'string';
    var colValue = isString ? value : value.column;
    var rowValue = isString ? value : value.row;
    return __assign(__assign({}, (colValue && (_a = {}, _a["--uitk-layoutgrid-column-gap"] = "var(--uitk-layoutgrid-space-" + colValue + ")", _a))), (rowValue && (_b = {}, _b["--uitk-layoutgrid-row-gap"] = "var(--uitk-layoutgrid-space-" + rowValue + ")", _b)));
};
/**
 * Set a valid value for the deprecated UitkLayoutGridAutoRepeat interface
 *
 * @param repeatType {string} Type of repeat
 * @param minValue {any} min value for the minmax() part of the repeat() function
 * @param suffix {string} suffix for the custom property name
 * @returns object as React.CSSProperties
 */
var autoRepeatGridValue = function (repeatType, minValue, suffix) {
    var _a;
    var newRepeatType = repeatType.replace('_', '-');
    return _a = {},
        _a["--uitk-layoutgrid-" + suffix] = "repeat(" + newRepeatType + ", minmax(var(--uitk-layoutgrid-egds-size__" + minValue + "), 1fr))",
        _a;
};
/**
 * Generate a valid CSS custom prop / value object for:
 *
 * - columns
 * - rows
 * - autoColumns
 * - autoRows
 *
 * @param value {UitkLayoutSpace | UitkLayoutGridSpaceInterface}
 * @returns Object as React.CSSProperties
 */
var gridTrackValue = function (value, suffix) {
    var _a;
    var isObject = !Array.isArray(value) && typeof value === 'object';
    var obj = {};
    if (isObject) {
        Object.entries(value).map(function (_a) {
            var _b;
            var key = _a[0], val = _a[1];
            return isAutoRepeatGrid(value)
                ? Object.assign(obj, autoRepeatGridValue(key, val, suffix))
                : Object.assign(obj, (_b = {}, _b["--uitk-layoutgrid-" + suffix + "-" + key] = trackList(val), _b));
        });
    }
    else {
        Object.assign(obj, (_a = {}, _a["--uitk-layoutgrid-" + suffix] = trackList(value), _a));
    }
    return obj;
};
/**
 * Prevent invalid CSS Custom Properties from being applied
 *
 * @param value {UitkLayoutGridArea | UitkLayoutConditionalGridArea | UitkLayoutGridTrack | UitkLayoutConditionalGridTrack}
 * @param suffix {string}
 * @param baseCSSClass {string}
 * @returns getClassNameList()
 */
var conditionalGridTrackClassName = function (value, suffix, baseCSSClass) {
    if (baseCSSClass === void 0) { baseCSSClass = baseClass; }
    var isObject = !Array.isArray(value) && typeof value === 'object';
    var isautoGrid = isObject &&
        (Object.prototype.hasOwnProperty.call(value, 'auto_fill') ||
            Object.prototype.hasOwnProperty.call(value, 'auto_fit'));
    // A small media query is treated as the default style, so
    // we need the same class name to be used for both
    var isSmallTrack = isObject && Object.prototype.hasOwnProperty.call(value, 'small');
    return getClassNameList$1(isObject
        ? Object.entries(value).map(function (_a) {
            var key = _a[0];
            return baseCSSClass + "-has-" + suffix + "-by-" + key;
        })
        : baseCSSClass + "-has-" + suffix, isSmallTrack && baseCSSClass + "-has-" + suffix, isautoGrid && baseCSSClass + "-has-" + suffix + "-using-auto-grid");
};
var wrapperClassNames = function (_a) {
    var areas = _a.areas, alignContent = _a.alignContent, alignItems = _a.alignItems, autoFlow = _a.autoFlow, autoRows = _a.autoRows, blockSize = _a.blockSize, className = _a.className, columns = _a.columns, display = _a.display, justifyContent = _a.justifyContent, justifyItems = _a.justifyItems, rows = _a.rows, space = _a.space;
    return getClassNameList$1(baseClass, alignContent && baseClass + "-align-content-" + alignContent, alignItems && baseClass + "-align-items-" + alignItems, areas && conditionalGridTrackClassName(areas, 'areas'), autoRows && baseClass + "-has-auto-rows", columns && conditionalGridTrackClassName(columns, 'columns'), rows && conditionalGridTrackClassName(rows, 'rows'), space && baseClass + "-has-space", autoFlow && baseClass + "-auto-flow-" + autoFlow, blockSize && baseClass + "-block-size-" + blockSize, display && baseClass + "-display-" + display, justifyContent && baseClass + "-justify-content-" + justifyContent, justifyItems && baseClass + "-justify-items-" + justifyItems, className);
};
var UitkLayoutGrid = function (props) {
    var alignContent = props.alignContent, alignItems = props.alignItems, areas = props.areas, autoColumns = props.autoColumns, autoFlow = props.autoFlow, autoRows = props.autoRows, blockSize = props.blockSize, children = props.children, display = props.display, space = props.space, columns = props.columns, className = props.className, justifyContent = props.justifyContent, justifyItems = props.justifyItems, rows = props.rows, tag = props.tag, dataTestId = props.dataTestId, style = props.style, attributes = __rest(props, ["alignContent", "alignItems", "areas", "autoColumns", "autoFlow", "autoRows", "blockSize", "children", "display", "space", "columns", "className", "justifyContent", "justifyItems", "rows", "tag", "dataTestId", "style"]);
    var wrapperClassNameProps = {
        alignContent: alignContent,
        alignItems: alignItems,
        autoFlow: autoFlow,
        autoRows: autoRows,
        areas: areas,
        blockSize: blockSize,
        className: className,
        columns: columns,
        display: display,
        justifyContent: justifyContent,
        justifyItems: justifyItems,
        rows: rows,
        space: space
    };
    var gridCustomProps = __assign(__assign(__assign(__assign(__assign(__assign(__assign({}, (areas && areasValue(areas))), (autoColumns && gridTrackValue(autoColumns, 'auto-columns'))), (autoRows && gridTrackValue(autoRows, 'auto-rows'))), (columns && gridTrackValue(columns, 'columns'))), (rows && gridTrackValue(rows, 'rows'))), (space && spaceValue(space))), (style && style));
    // Renders the custom Tag
    var Tag = ((tag && UitkLayoutTags.get(tag)) || UitkLayoutTags.get('div'));
    return children ? (React.createElement(Tag, __assign({ className: wrapperClassNames(__assign({}, wrapperClassNameProps)), style: gridCustomProps }, attributes, (dataTestId && { 'data-test-id': dataTestId })), children)) : null;
};
UitkLayoutGrid.displayName = 'UitkLayoutGrid';
UitkLayoutGrid.defaultProps = {
    autoColumns: [MinMax('0x', '1fr')],
    display: 'grid'
};
var withUitkLayoutGrid = function (WrappedComponent) {
    var WithUitkLayoutGrid = function (props) {
        var alignContent = props.alignContent, alignItems = props.alignItems, autoColumns = props.autoColumns, autoFlow = props.autoFlow, autoRows = props.autoRows, areas = props.areas, blockSize = props.blockSize, className = props.className, children = props.children, columns = props.columns, display = props.display, justifyContent = props.justifyContent, justifyItems = props.justifyItems, rows = props.rows, space = props.space, style = props.style;
        var wrapperClassNameProps = {
            alignContent: alignContent,
            alignItems: alignItems,
            autoFlow: autoFlow,
            blockSize: blockSize,
            columns: columns,
            className: className,
            display: display,
            justifyContent: justifyContent,
            justifyItems: justifyItems,
            rows: rows
        };
        var gridCustomProps = __assign(__assign(__assign(__assign(__assign(__assign(__assign({}, (areas && areasValue(areas))), (autoColumns && gridTrackValue(autoColumns, 'auto-columns'))), (autoRows && gridTrackValue(autoRows, 'auto-rows'))), (columns && gridTrackValue(columns, 'columns'))), (rows && gridTrackValue(rows, 'rows'))), (space && spaceValue(space))), (style && style));
        return (React.createElement(WrappedComponent, { className: wrapperClassNames(__assign({}, wrapperClassNameProps)), style: gridCustomProps }, children || null));
    };
    WithUitkLayoutGrid.displayName = "withUitkLayoutGrid(" + WrappedComponent.displayName + ")";
    return WithUitkLayoutGrid;
};

var getClassNameList = classNames.bind(styles);
var UitkLayoutGridItem = function (props) {
    var _a;
    var alignSelf = props.alignSelf, area = props.area, className = props.className, colEnd = props.colEnd, colSpan = props.colSpan, colStart = props.colStart, justifySelf = props.justifySelf, rowSpan = props.rowSpan, rowStart = props.rowStart, rowEnd = props.rowEnd, style = props.style;
    var childrenArray = React.Children.toArray(props.children);
    if (!childrenArray.length) {
        return null;
    }
    var firstChild = childrenArray[0];
    var firstChildClassName = firstChild.props.className;
    var firstChildStyle = firstChild.props.style;
    var baseClass = 'uitk-layout-grid-item';
    var itemCustomPropValue = function (suffix, value, isSpan) {
        var _a, _b, _c;
        var isEnd = value === 'end';
        var isAll = value === 'all';
        var returnValue = {};
        if (isEnd) {
            returnValue = (_a = {},
                _a["--uitk-layoutgrid-" + suffix] = "-1",
                _a);
        }
        else if (isAll) {
            returnValue = (_b = {},
                _b["--uitk-layoutgrid-" + suffix] = "1",
                _b["--uitk-layoutgrid-" + suffix.replace('start', 'end')] = "-1",
                _b);
        }
        else {
            returnValue = (_c = {},
                _c["--uitk-layoutgrid-" + suffix] = isSpan ? "span " + value : "" + value,
                _c);
        }
        return returnValue;
    };
    var MakeCustomProp = function (suffix, value, isSpan) {
        var isObject = !Array.isArray(value) && typeof value === 'object';
        var obj = {};
        if (isObject) {
            Object.entries(value).map(function (_a) {
                var key = _a[0], val = _a[1];
                return Object.assign(obj, itemCustomPropValue(suffix + "-" + key, val, isSpan));
            });
        }
        else {
            Object.assign(obj, itemCustomPropValue(suffix, value, isSpan));
        }
        return obj;
    };
    /**
     * Prevent invalid CSS Custom Properties from being applied
     *
     * @param value {UitkLayoutGridItemSpanValues | UitkLayoutConditionalGridSpan | UitkLayoutGridItemStartEndValues | UitkLayoutConditionalGridStartEnd}
     * @param suffix {string}
     * @param baseCSSClass {string}
     * @returns getClassNameList()
     */
    var conditionalGridItemClassName = function (value, suffix, baseCSSClass) {
        if (baseCSSClass === void 0) { baseCSSClass = baseClass; }
        var isObject = !Array.isArray(value) && typeof value === 'object';
        // A small media query is treated as the default style, so
        // we need the same class name to be used for both
        var isSmallValue = isObject && Object.prototype.hasOwnProperty.call(value, 'small');
        return getClassNameList(isObject
            ? Object.entries(value).map(function (_a) {
                var key = _a[0];
                return baseCSSClass + "-has-" + suffix + "-by-" + key;
            })
            : baseCSSClass + "-has-" + suffix, isSmallValue && baseCSSClass + "-has-" + suffix);
    };
    var wrapperClassNames = getClassNameList(baseClass, firstChildClassName, alignSelf && baseClass + "-align-self-" + alignSelf, area &&
        baseClass + "-has-column-start " + baseClass + "-has-column-end " + baseClass + "-has-row-start " + baseClass + "-has-row-end", colStart && conditionalGridItemClassName(colStart, 'column-start'), colEnd && conditionalGridItemClassName(colEnd, 'column-end'), colSpan && conditionalGridItemClassName(colSpan, 'column-start'), colSpan && colSpan === 'all' && conditionalGridItemClassName(colSpan, 'column-end'), rowStart && conditionalGridItemClassName(rowStart, 'row-start'), rowEnd && conditionalGridItemClassName(rowEnd, 'row-end'), rowSpan && conditionalGridItemClassName(rowSpan, 'row-start'), rowSpan && rowSpan === 'all' && conditionalGridItemClassName(rowSpan, 'row-end'), justifySelf && baseClass + "-justify-self-" + justifySelf, className);
    var gridItemCustomProps = __assign(__assign(__assign(__assign({}, (area && (_a = {},
        _a["--uitk-layoutgrid-column-start"] = area + "-start",
        _a["--uitk-layoutgrid-column-end"] = area + "-end",
        _a["--uitk-layoutgrid-row-start"] = area + "-start",
        _a["--uitk-layoutgrid-row-end"] = area + "-end",
        _a))), (!area && __assign(__assign(__assign(__assign(__assign(__assign({}, (colSpan && MakeCustomProp('column-start', colSpan, true))), (colStart && MakeCustomProp('column-start', colStart))), (colEnd && MakeCustomProp('column-end', colEnd))), (rowSpan && MakeCustomProp('row-start', rowSpan, true))), (rowStart && MakeCustomProp('row-start', rowStart))), (rowEnd && MakeCustomProp('row-end', rowEnd))))), (firstChildStyle && firstChildStyle)), (style && style));
    var decoratedChild = React.cloneElement(firstChild, {
        className: wrapperClassNames,
        style: gridItemCustomProps
    });
    return decoratedChild;
};
UitkLayoutGridItem.displayName = 'UitkLayoutGridItem';

export { EgdsSizeCustomProp, EgdsSizingLengths, FitContent, IsEgdsSize, MinMax, Repeat, UitkLayoutGrid, UitkLayoutGridAligns, UitkLayoutGridAutoFlows, UitkLayoutGridBlockSizes, UitkLayoutGridColumnRows, UitkLayoutGridItem, UitkLayoutGridItemPlaceSelfs, UitkLayoutGridPlaces, UitkLayoutGridSpans, isAutoRepeatGrid, withUitkLayoutGrid };
